import {
  BreakpointObserver,
  Breakpoints,
  LayoutModule,
} from '@angular/cdk/layout';
import { Component, DestroyRef, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive, RouterModule } from '@angular/router';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { ThemeService } from '../../../core/services/theme.service';
import { UserHttpService } from '../../../core/services/user.http.service';

type NavItem = {
  label: string;
  icon: string;
  route: string;
};

@Component({
  selector: 'app-sidenav',
  imports: [
    LayoutModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatListModule,
    MatDividerModule,
    MatMenuModule,
    RouterLink,
    RouterLinkActive,
    RouterModule,
  ],
  templateUrl: './sidenav.html',
  styleUrl: './sidenav.scss',
})
export class Sidenav {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly httpService = inject(UserHttpService);

  sidenav = viewChild<MatSidenav>('sidenav');
  readonly appTitle = 'SNF Dashboard';

  readonly navItems: NavItem[] = [
    { label: 'Statistics', icon: 'assessment', route: '/dashboard' },
    { label: 'Users', icon: 'group', route: '/users' },
    { label: 'Deep Reports', icon: 'description', route: '/deep-reports' },
    { label: 'Juror Reports', icon: 'gavel', route: '/juror-reports' },
    { label: 'Invoices', icon: 'receipt_long', route: '/invoices' },
    { label: 'Coupons', icon: 'sell', route: '/coupons' },
    { label: 'User Activities', icon: 'history', route: '/user-activities' },
  ];

  readonly isHandset = signal(false);
  readonly isCollapsed = signal(true);

  readonly pageTitle = signal('Overview');
  readonly userName = signal('Loading…');
  readonly userEmail = signal('');
  readonly authLoading = this.httpService.authLoading;
  readonly isDarkMode = this.themeService.isDarkMode;

  constructor(private destroyRef: DestroyRef) {
    effect(() => {
      const currentUser = this.httpService.currentUser();
      const authLoading = this.authLoading();

      if (authLoading) {
        this.userName.set('Loading…');
        this.userEmail.set('');
        return;
      }

      if (currentUser) {
        this.userName.set(currentUser.full_name || 'Unknown User');
        this.userEmail.set(currentUser.email || 'No Email');
      } else {
        this.userName.set('Guest');
        this.userEmail.set('');
      }
    });
    this.breakpointObserver
      .observe(Breakpoints.Handset)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.isHandset.set(result.matches));

    this.updatePageTitle();
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updatePageTitle());

    this.syncCurrentUserFromProfile();

  }

  closeOnMobile(): void {
    if (this.isHandset()) {
      this.sidenav()?.close();
    }
  }

  toggleDesktopSidenav(): void {
    this.isCollapsed.update((isCollapsed) => !isCollapsed);
  }

  goHome(): void {
    this.router.navigate(['/dashboard']);
  }

  goProfile(): void {
    this.router.navigate(['/profile']);
  }

  logout(): void {
    this.httpService.logout();
    this.router.navigate(['/login']);
  }

  toggleThemeMode(): void {
    this.themeService.toggleThemeMode();
  }

  private updatePageTitle(): void {
    if (this.router.url.startsWith('/profile')) {
      this.pageTitle.set('Profile');
      return;
    }

    const currentItem = this.navItems.find((item) =>
      this.router.url.startsWith(item.route),
    );
    this.pageTitle.set(currentItem?.label ?? 'Overview');
  }

  private syncCurrentUserFromProfile(): void {
    const currentUser = this.httpService.currentUser();
    const userId = String(currentUser?._id ?? currentUser?.id ?? currentUser?.sub ?? '');
    if (!userId) {
      return;
    }

    this.httpService
      .getProfile(userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const profile = res?.profile;
          if (!profile) {
            return;
          }

          this.httpService.patchCurrentUser({
            full_name: String(profile?.full_name ?? currentUser?.full_name ?? ''),
            email: String(profile?.email ?? currentUser?.email ?? ''),
          });
        },
      });
  }
}
